<script lang="ts">
  import { onDestroy } from 'svelte';
  import { t } from '../../i18n/index.svelte';
  import { applyPreset, type PresetKind } from '../../scene/presets';
  import { sceneStore } from '../../scene/store.svelte';
  import { evaluate } from '../../scene/timeline';
  import { CAMERA_TRACK, type Easing } from '../../scene/types';

  const scene = $derived(sceneStore.current);
  const target = $derived(sceneStore.selected);
  const track = $derived(scene.timeline.tracks.find((tr) => tr.target === target));
  const keys = $derived(track ? [...track.keys].sort((a, b) => a.time - b.time) : []);

  let playing = $state(false);
  let raf = 0;
  let last = 0;

  function tick(now: number) {
    if (!playing) return;
    const d = scene.timeline.duration || 1;
    sceneStore.time = (sceneStore.time + (now - last) / 1000) % d;
    last = now;
    raf = requestAnimationFrame(tick);
  }

  function toggle() {
    playing = !playing;
    if (playing) {
      last = performance.now();
      raf = requestAnimationFrame(tick);
    } else cancelAnimationFrame(raf);
  }
  onDestroy(() => cancelAnimationFrame(raf));

  /** Adds/replaces a key at the current time with the current pose. */
  function addKey() {
    if (!target) return;
    const time = Math.round(sceneStore.time * 100) / 100;
    const p = evaluate(scene, sceneStore.time);
    sceneStore.update((d) => {
      let tr = d.timeline.tracks.find((x) => x.target === target);
      if (!tr) {
        tr = { target, keys: [] };
        d.timeline.tracks.push(tr);
      }
      tr.keys = tr.keys.filter((k) => Math.abs(k.time - time) > 1e-3);
      if (target === CAMERA_TRACK)
        tr.keys.push({ time, position: p.camera.position, target: p.camera.target, easing: 'ease_in_out' });
      else {
        const l = p.local[target];
        tr.keys.push({ time, position: l.position, rotation: l.rotation, scale: l.scale, easing: 'ease_in_out' });
      }
    });
  }

  function removeKey(time: number) {
    sceneStore.update((d) => {
      const tr = d.timeline.tracks.find((x) => x.target === target);
      if (tr) tr.keys = tr.keys.filter((k) => k.time !== time);
      d.timeline.tracks = d.timeline.tracks.filter((x) => x.keys.length);
    });
  }

  function setEasing(time: number, e: Easing) {
    sceneStore.update((d) => {
      const k = d.timeline.tracks.find((x) => x.target === target)?.keys.find((k) => k.time === time);
      if (k) k.easing = e;
    });
  }

  function preset(kind: PresetKind) {
    sceneStore.update((d) => applyPreset(d, kind, target === CAMERA_TRACK ? null : target));
  }

  const comps = (k: (typeof keys)[number]) =>
    (['position', 'rotation', 'scale', 'target'] as const)
      .filter((c) => k[c])
      .map((c) => t(`scene.k.${c}`))
      .join(', ');
</script>

<div class="timeline" data-testid="timeline">
  <div class="row bar">
    <button onclick={toggle} data-testid="timeline-play" aria-label={playing ? t('player.pause') : t('player.play')}
      >{playing ? '⏸' : '▶'}</button
    >
    <input
      type="range"
      min="0"
      max={scene.timeline.duration}
      step="0.01"
      bind:value={sceneStore.time}
      oninput={() => (playing = false)}
      data-testid="timeline-time"
    />
    <span class="mono small">{sceneStore.time.toFixed(2)} / </span>
    <input
      class="dur"
      type="number"
      min="0.5"
      max="120"
      step="0.5"
      value={scene.timeline.duration}
      onchange={(e) =>
        sceneStore.update((d) => (d.timeline.duration = Number((e.currentTarget as HTMLInputElement).value)))}
      title={t('anim.duration')}
      data-testid="timeline-duration"
    />
    <span class="small">s</span>
    <span class="grow"></span>
    {#if target}
      <select
        onchange={(e) => {
          const v = (e.currentTarget as HTMLSelectElement).value as PresetKind;
          if (v) preset(v);
          (e.currentTarget as HTMLSelectElement).value = '';
        }}
        data-testid="timeline-preset"
      >
        <option value="">{t('scene.presets')}</option>
        {#if target === CAMERA_TRACK}
          <option value="flythrough">{t('scene.preset.flythrough')}</option>
        {:else}
          {#each ['spin', 'jump', 'pulse', 'wave'] as const as p (p)}<option value={p}>{t(`scene.preset.${p}`)}</option
            >{/each}
        {/if}
      </select>
      <button onclick={addKey} data-testid="timeline-add-key">◆ {t('scene.addKey')}</button>
    {/if}
  </div>
  {#if target}
    <div class="keys">
      {#each keys as k (k.time)}
        <div class="key row">
          <button class="mono t" onclick={() => (sceneStore.time = k.time)}>{k.time.toFixed(2)}s</button>
          <span class="muted comps">{comps(k)}</span>
          <select
            value={k.easing ?? 'linear'}
            onchange={(e) => setEasing(k.time, (e.currentTarget as HTMLSelectElement).value as Easing)}
          >
            {#each ['linear', 'ease_in', 'ease_out', 'ease_in_out', 'step'] as const as e (e)}<option value={e}
                >{t(`scene.ease.${e}`)}</option
              >{/each}
          </select>
          <button class="x" onclick={() => removeKey(k.time)} title={t('common.remove')}>×</button>
        </div>
      {:else}
        <p class="muted small">{t('scene.noKeys')}</p>
      {/each}
    </div>
  {:else}
    <p class="muted small">{t('scene.selectForKeys')}</p>
  {/if}
</div>

<style>
  .timeline {
    border-top: 1px solid var(--line);
    background: var(--panel);
    padding: 8px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 190px;
  }
  .bar input[type='range'] {
    flex: 1;
    max-width: 560px;
  }
  .dur {
    width: 64px;
    padding: 2px 4px;
  }
  .grow {
    flex: 1;
  }
  .keys {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .key {
    gap: 8px;
    font-size: 12px;
  }
  .key .t {
    padding: 1px 6px;
    font-size: 12px;
  }
  .comps {
    flex: 1;
  }
  .key select {
    width: auto;
    padding: 1px 4px;
    font-size: 12px;
  }
  .x {
    padding: 0 6px;
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
  select {
    width: auto;
  }
</style>
